/**
 * Left-rail navigation, used on md+ viewports.
 *
 * Wordmark at top, nav items in the middle, identity + sign out at the
 * bottom. Notification bell sits in the lower group with an unread
 * counter dot. Clicking it asks the parent Layout to open the drawer.
 */
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { classNames } from '@/lib/format';
import { useStickyToggle } from '@/lib/useStickyToggle';
import { useCommandPalette } from './CommandPalette';
import { Monomark } from './Logo';
import {
  IconAccounting,
  IconBell,
  IconBusiness,
  IconPipeline,
  IconArollSaas,
  IconClients,
  IconContractors,
  IconDocs,
  IconDashboard,
  IconFolio,
  IconInbox,
  IconJobs,
  IconLaunch,
  IconSettings,
  IconSignOut,
  IconSubscriptions,
  IconTasks,
  IconTimelines,
} from './Icons';

export function Sidebar({
  unreadCount,
  onOpenNotifications,
  themeToggle,
  syncControl,
}: {
  unreadCount: number;
  onOpenNotifications: () => void;
  themeToggle?: React.ReactNode;
  syncControl?: React.ReactNode;
}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const palette = useCommandPalette();
  // Desktop-only collapse: hide the rail entirely for a wider work area,
  // with a small floating button to pop it back out. Persists across
  // navigation + reload.
  const [collapsed, toggleCollapsed] = useStickyToggle('kg.sidebar.collapsed', false);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const isMac =
    typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

  return (
    <>
      {/* Floating control to reopen the rail. Always mounted so it can
          fade in / out; only interactive (and visible) while collapsed.
          The fade is delayed on the way in so it lands after the rail has
          finished sliding away. md+ only — mobile uses the top bar +
          drawer. */}
      <button
        type="button"
        onClick={toggleCollapsed}
        aria-label="Expand sidebar"
        title="Expand sidebar"
        className={classNames(
          'hidden md:flex fixed top-3 left-3 z-40 items-center justify-center w-9 h-9 kg-glass rounded-lg text-kg-grey-600 hover:text-kg-black dark:text-kg-grey-300 dark:hover:text-white shadow-kg-xs transition-opacity duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-kg-black bg-kg-grey-100/70 dark:bg-white/[0.05]',
          collapsed ? 'opacity-100 delay-150' : 'opacity-0 pointer-events-none',
        )}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M13 6l6 6-6 6" />
          <path d="M6 6l6 6-6 6" />
        </svg>
      </button>

      {/* The rail. Its width animates between full and zero so it slides
          open / closed; overflow-hidden clips the fixed-width inner panel
          during the slide so the content doesn't reflow as it narrows. */}
      <aside
        className={classNames(
          // LOW CHROME: the rail used to be told apart from the page by a
          // hairline down its right edge — a line on every screen in the
          // app, reporting nothing. It's now told apart by GROUND: paper
          // over the warm grey page on light, a lifted tonal fill over
          // black on dark (kg-glass alone was black/60 on black, which is
          // no step at all, which is why the border was there).
          'hidden md:block md:shrink-0 md:h-screen md:sticky md:top-0 overflow-hidden kg-rail transition-[width] duration-300 ease-kg-out',
          collapsed ? 'md:w-0' : 'md:w-60',
        )}
      >
        <div className="flex h-full w-60 flex-col">
      {/* Logo, search and the footer group are separated by SPACING, not
          by rules. Three stacked hairlines in a 240px rail was the
          densest line work left in the shell. */}
      <div className="px-3 h-16 flex items-center justify-between gap-2">
        <Link
          to="/"
          className="flex items-center pl-3"
          aria-label="Kilogram dashboard"
        >
          <Monomark variant="black" className="h-8 w-auto dark:hidden" />
          <Monomark variant="white" className="h-8 w-auto hidden dark:inline-block" />
        </Link>
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-label="Collapse sidebar"
          title="Collapse sidebar"
          className="p-2 text-kg-grey-500 hover:text-kg-black dark:text-kg-grey-400 dark:hover:text-white rounded-lg hover:bg-kg-grey-100/80 dark:hover:bg-white/10 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-kg-black"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M11 6l-6 6 6 6" />
            <path d="M18 6l-6 6 6 6" />
          </svg>
        </button>
      </div>

      <div className="px-3 py-3">
        <button
          onClick={palette.open}
          className="w-full flex items-center gap-2 px-3.5 py-2 text-xs text-kg-grey-600 bg-kg-grey-50/80 hover:bg-white hover:shadow-kg-xs dark:bg-kg-grey-900/60 dark:text-kg-grey-300 dark:hover:bg-kg-grey-900 transition-all duration-200 ease-kg-out rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-kg-black"
        >
          <span className="flex-1 text-left">Search or jump to...</span>
          <span className="kg-kbd">{isMac ? '⌘K' : 'Ctrl K'}</span>
        </button>
      </div>

      <nav aria-label="Main" className="flex-1 px-3 py-4 space-y-1">
        <MainNavItems role={user?.role} isOwner={user?.isOwner} />
      </nav>

      <div className="px-3 pb-3 pt-4">
        {syncControl}
        <button
          onClick={onOpenNotifications}
          className="w-full flex items-center gap-3 px-3 py-2.5 text-xs uppercase tracking-widest text-kg-grey-700 hover:text-kg-black hover:bg-kg-grey-50 dark:text-kg-grey-300 dark:hover:text-white dark:hover:bg-kg-grey-800 transition-colors rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-kg-black"
        >
          <span className="relative">
            <IconBell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span
                aria-hidden="true"
                className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-700 rounded-full"
              />
            )}
          </span>
          <span className="flex-1 text-left">Notifications</span>
          {unreadCount > 0 && (
            <span className="text-xs text-kg-grey-600 dark:text-kg-grey-400">{unreadCount}</span>
          )}
        </button>
        {themeToggle && (
          <div className="mt-2 flex items-center justify-between px-3 py-1.5 text-xs uppercase tracking-widest text-kg-grey-700 dark:text-kg-grey-300">
            <span>Theme</span>
            {themeToggle}
          </div>
        )}
      </div>

      {/* The account footer is the one group in the rail that earns
          separation (it's a different kind of thing from navigation), so
          it keeps it — as a change in GROUND rather than a rule. */}
      <div className="px-6 py-4 bg-kg-grey-100/70 dark:bg-white/[0.04]">
        {user && (
          <div className="mb-3">
            <p className="text-sm font-medium text-kg-black dark:text-white truncate">{user.name}</p>
            {/* Role label. Amber for EDITOR so it's visually obvious
                when logged in as the restricted account (no quotes,
                no invoices visible). Helps catch wrong-account
                situations at a glance. */}
            <p
              className={
                'text-xs uppercase tracking-widest mt-0.5 ' +
                (user.role === 'EDITOR'
                  ? 'text-amber-600 dark:text-amber-400 font-semibold'
                  : 'text-kg-grey-500 dark:text-kg-grey-400')
              }
            >
              {user.role === 'EDITOR' ? 'Editor (restricted)' : user.role.toLowerCase()}
            </p>
          </div>
        )}
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2 text-xs uppercase tracking-widest text-kg-grey-700 hover:text-kg-black dark:text-kg-grey-300 dark:hover:text-white transition-colors py-1"
        >
          <IconSignOut className="w-4 h-4" />
          Sign out
        </button>
      </div>
        </div>
      </aside>
    </>
  );
}

/**
 * The main navigation list, shared between the desktop sidebar and the
 * mobile nav drawer so the two can never drift. Role gating lives here:
 * Folio for ADMIN/EDITOR, Accounting for ADMIN only.
 * `onNavigate` lets the mobile drawer close itself when a link is tapped.
 */
export function MainNavItems({
  role,
  isOwner,
  onNavigate,
}: {
  role?: string;
  isOwner?: boolean;
  onNavigate?: () => void;
}) {
  // The rail had grown to sixteen flat items. The daily working set
  // stays flat at the top; everything you visit weekly-not-hourly
  // (people admin, folio, money surfaces) lives in a collapsible
  // "Studio" group. Default expanded so nothing moves until you tuck
  // it away yourself; the choice persists. When collapsed, the active
  // item (if it's in the group) stays rendered so you never lose your
  // place.
  const [studioOpen, toggleStudio] = useStickyToggle('kg.nav.studio', true);
  const location = useLocation();

  // Same role gating as before, expressed as data so the collapsed
  // state can still find the active item.
  const studioItems: Array<{ to: string; label: string; icon: React.ReactNode }> = [
    { to: '/contractors', label: 'Contractors', icon: <IconContractors className="w-5 h-5" /> },
    // Studio docs (SOPs / how-tos) — every staff login reads; the
    // backend gates writing to the owner.
    { to: '/docs', label: 'Docs', icon: <IconDocs className="w-5 h-5" /> },
    // Pitches — the new-business workspace. Team-visible: no money on
    // it, and an editor can carry a pitch through the pipeline.
    ...(role === 'ADMIN' || role === 'EDITOR'
      ? [{ to: '/pitches', label: 'Pitches', icon: <IconPipeline className="w-5 h-5" /> }]
      : []),
    // Folio — ADMIN + EDITOR. The Content page left the sidebar on
    // 2026-09-22: its lane merged into the dashboard's Launch lane and
    // its queue into each job's Launch tab. /content still serves the
    // agent-activity panel, linked from Settings.
    ...(role === 'ADMIN' || role === 'EDITOR'
      ? [{ to: '/folio', label: 'Folio', icon: <IconFolio className="w-5 h-5" /> }]
      : []),
    // Money surfaces — owner-only (Business incl. the deal funnel, the
    // Coach and Quiet Days, which are views inside the Business page,
    // Accounting, Subscriptions spend). Hidden from EDITOR.
    // Enquiries rides with them: website leads are owner-only for the
    // same reason the enquiry bell is (Nick's call, not the team's).
    ...(isOwner
      ? [
          { to: '/enquiries', label: 'Enquiries', icon: <IconInbox className="w-5 h-5" /> },
          { to: '/business-manager', label: 'Business', icon: <IconBusiness className="w-5 h-5" /> },
          { to: '/pictureup', label: 'PictureUp', icon: <IconArollSaas className="w-5 h-5" /> },
          // In-app accounting hub — LIVE. Quotes + invoices are
          // CRM-native with frozen PDFs, pushed to Xero.
          { to: '/accounting', label: 'Accounting', icon: <IconAccounting className="w-5 h-5" /> },
          { to: '/subscriptions', label: 'Subscriptions', icon: <IconSubscriptions className="w-5 h-5" /> },
        ]
      : []),
  ];
  const activeStudioItem = studioItems.find((i) => location.pathname.startsWith(i.to)) ?? null;

  return (
    <>
      <NavItem to="/" label="Dashboard" icon={<IconDashboard className="w-5 h-5" />} end onNavigate={onNavigate} />
      <NavItem to="/jobs" label="Jobs" icon={<IconJobs className="w-5 h-5" />} onNavigate={onNavigate} />
      {/* Cross-job to-do list — ADMIN + EDITOR. Every task across every
          job (client + KG), grouped by person, tick-off-able. */}
      {(role === 'ADMIN' || role === 'EDITOR') && (
        <NavItem to="/tasks" label="Tasks" icon={<IconTasks className="w-5 h-5" />} onNavigate={onNavigate} />
      )}
      {/* Whole-team delivery overview — ADMIN + EDITOR (never clients;
          clients don't reach this app). Not owner-gated: delivery load
          is a team question and the payload carries no money. */}
      {(role === 'ADMIN' || role === 'EDITOR') && (
        <NavItem to="/timelines" label="Timelines" icon={<IconTimelines className="w-5 h-5" />} onNavigate={onNavigate} />
      )}
      {/* Cross-job launch board — ADMIN + EDITOR. Every scheduled post
          across every job, plus one row per launch. Changes happen on
          the job's Launch tab; this is the overview (Nick, 2026-09-24). */}
      {(role === 'ADMIN' || role === 'EDITOR') && (
        <NavItem to="/launch" label="Launch" icon={<IconLaunch className="w-5 h-5" />} onNavigate={onNavigate} />
      )}
      <NavItem to="/clients" label="Clients" icon={<IconClients className="w-5 h-5" />} onNavigate={onNavigate} />
      <NavItem to="/inbox" label="Inbox" icon={<IconInbox className="w-5 h-5" />} onNavigate={onNavigate} />

      {studioItems.length > 0 && (
        <>
          <button
            type="button"
            onClick={toggleStudio}
            aria-expanded={studioOpen}
            className="w-full flex items-center justify-between px-3.5 pt-5 pb-1.5 text-[10px] uppercase tracking-[0.2em] text-kg-grey-500 dark:text-kg-grey-400 hover:text-kg-black dark:hover:text-white transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-kg-black"
          >
            Studio
            <svg
              viewBox="0 0 24 24"
              width="12"
              height="12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              className={classNames('transition-transform duration-200', !studioOpen && '-rotate-90')}
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          {(studioOpen ? studioItems : activeStudioItem ? [activeStudioItem] : []).map((i) => (
            <NavItem key={i.to} to={i.to} label={i.label} icon={i.icon} onNavigate={onNavigate} />
          ))}
        </>
      )}

      <NavItem to="/settings" label="Settings" icon={<IconSettings className="w-5 h-5" />} onNavigate={onNavigate} />
    </>
  );
}

function NavItem({
  to,
  label,
  icon,
  end,
  onNavigate,
}: {
  to: string;
  label: string;
  icon: React.ReactNode;
  end?: boolean;
  onNavigate?: () => void;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        classNames(
          'flex items-center gap-3 px-3.5 py-2.5 text-xs uppercase tracking-widest rounded-xl',
          'transition-all duration-200 ease-kg-out active:scale-[0.98]',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-kg-black',
          isActive
            ? 'bg-kg-black text-white shadow-kg-sm dark:bg-white dark:text-kg-black'
            : 'text-kg-grey-700 hover:text-kg-black hover:bg-kg-grey-100/80 dark:text-kg-grey-300 dark:hover:text-white dark:hover:bg-white/10',
        )
      }
    >
      {icon}
      {label}
    </NavLink>
  );
}
